// Catalog.ID Client — Leaving this device
// ============================================================================
// Two distinct exits, decided 2026-09-03:
//
//   Log Out            ends the SESSIONS of every identity stored on this
//                      device and de-enrols nothing. The device record and the
//                      device-encrypted key bag stay, so the next login here
//                      is password (+ PIN) — exactly what the printed
//                      credentials page promises.
//
//   Forget this device does everything Log Out does AND de-enrols every
//                      identity: the server device record goes, and so do the
//                      local encrypted keys, PIN length, mode flags, key-grant
//                      decisions and module registrations. The next login here
//                      needs the supersecret for every identity.
//
// The old Settings "Log Out" did a confused half of each: it revoked only the
// active identity's session and device record, then wiped EVERY identity's
// session token. A delegate logout left the personal account enrolled but
// session-less, and any logout cost the supersecret on the next login.
//
// Both functions are DOM-free and take their world as injected dependencies,
// so the sequence is testable without a browser.

/** One identity's stored session token. */
export interface StoredSession {
    cid: string;
    token: string;
}

export interface DeviceSessionDeps {
    /** Every identity that has a stored session token, with that token. */
    listStoredSessions(): StoredSession[];
    /** Every identity enrolled here (device token and/or device-encrypted keys). */
    listEnrolledIdentities(): string[];
    /** The device token stored for one identity, or null when not enrolled. */
    getDeviceToken(cid: string): string | null;
    /** `POST /api/account/logout` authenticated with THAT identity's token, at
     *  THAT identity's origin — each session lives where it was created
     *  (docs/cid-governance.md D18), so the sweep may speak to several nodes. */
    logout(sessionToken: string, cid: string): Promise<void>;
    /** Remove the server device record, authenticated with THAT identity's
     *  token, at that identity's origin (where its device rows live). */
    removeDevice(deviceToken: string, sessionToken: string, cid: string): Promise<void>;
    /** Wipe device token, device id, PIN length, mode flag and keys for one identity. */
    clearDeviceStorage(cid: string): void;
    /** Wipe persistent key-grant decisions and module registrations for one identity. */
    clearIdentityGrants(cid: string): void;
    /** In-memory teardown plus the session-token / active-cid wipe. */
    clearInMemoryState(): void;
}

export interface LogoutAllResult {
    /** Identities the server confirmed logged out. */
    endedSessions: string[];
    /**
     * Identities whose logout call failed — a dead token (401) or an offline
     * server. Local state is cleared regardless; the token is worthless here
     * either way.
     */
    failedLogouts: string[];
}

export interface ForgetDeviceResult extends LogoutAllResult {
    /** Identities whose local device material was wiped. */
    deEnrolled: string[];
    /**
     * Identities whose SERVER device record could not be removed from here —
     * they were enrolled but had no usable session token, or the call failed.
     * The user must revoke those from that identity's Devices pane.
     */
    serverRecordsLeft: string[];
}

/**
 * End the sessions of every identity on this device; keep every enrolment.
 *
 * Each stored token gets its own `logout` call — one identity's dead token
 * (401) must not abort the sweep over the others. `clearInMemoryState` runs
 * last and is what removes the stored tokens and the active cid.
 */
export async function logoutAllIdentities(deps: DeviceSessionDeps): Promise<LogoutAllResult> {
    const result = await endAllSessions(deps);
    deps.clearInMemoryState();
    return result;
}

/**
 * Log out AND de-enrol every identity on this device.
 *
 * Order matters: the device-record removals go first, while the session tokens
 * are still live — logging out first would invalidate the very tokens those
 * calls authenticate with. Local material is wiped afterwards, whether or not
 * the server accepted the removal, so this device really does forget the keys.
 */
export async function forgetThisDevice(deps: DeviceSessionDeps): Promise<ForgetDeviceResult> {
    const sessions = deps.listStoredSessions();
    const tokenFor = new Map(sessions.map(s => [s.cid, s.token]));
    const enrolled = deps.listEnrolledIdentities();

    const serverRecordsLeft: string[] = [];
    for (const cid of enrolled) {
        const deviceToken = deps.getDeviceToken(cid);
        // No device token means no server record for this browser — only local
        // key material, so there is nothing to leave behind.
        if (!deviceToken) continue;
        const sessionToken = tokenFor.get(cid);
        if (!sessionToken) {
            serverRecordsLeft.push(cid);
            continue;
        }
        try {
            await deps.removeDevice(deviceToken, sessionToken, cid);
        } catch {
            serverRecordsLeft.push(cid);
        }
    }

    const sessionResult = await endAllSessions(deps);

    // Everything identity-scoped on this browser goes: the enrolled identities
    // plus any that only had a session token.
    const all = new Set<string>([...enrolled, ...sessions.map(s => s.cid)]);
    for (const cid of all) {
        deps.clearDeviceStorage(cid);
        deps.clearIdentityGrants(cid);
    }

    deps.clearInMemoryState();

    return {
        ...sessionResult,
        deEnrolled: enrolled,
        serverRecordsLeft,
    };
}

/**
 * Removing THIS browser's device record, which signs the browser out
 * (device-setup-restructure §15 B, decided 2026-09-18).
 *
 * `removeCurrentDeviceRecord` is injected rather than derived from `cid`
 * because the two call sites address the SAME row differently: the Devices
 * pane knows its database id (`DELETE /api/device/{id}`) and the "No local
 * storage" choice knows its token (`DELETE /api/device`).
 */
export interface RemoveCurrentDeviceDeps extends DeviceSessionDeps {
    /** Delete this browser's device row for the active identity, authenticated
     *  with the active session. Throwing aborts the whole operation. */
    removeCurrentDeviceRecord(): Promise<void>;
}

/**
 * Remove this browser's device record for one identity and sign the browser
 * out.
 *
 * Deleting the row destroys the encryption key that opens this browser's copy
 * of the bag, so the copy is dead the moment the call returns. Until
 * 2026-09-18 that was the whole action: the session token stayed in
 * localStorage and the decrypted bag stayed in memory, so the tab went on
 * working — and a walk-up could set the device straight back up from that
 * memory, in any mode they liked. That is exactly the bypass the downgrade gate
 * on `PUT /api/device/pin` (§15 A) would otherwise leave open, because
 * `POST /api/device/setup` is deliberately ungated. Ending the session closes
 * it: the only way back to keys on this browser is the password plus the
 * supersecret.
 *
 * Order and failure rules:
 *  * the server row goes FIRST, while the token authenticating that call is
 *    still live. A failure there aborts and is re-thrown with nothing touched,
 *    so the caller can leave the row on screen and say why.
 *  * this identity's local device material goes next.
 *  * every session on the browser ends last, exactly as {@link logoutAllIdentities}
 *    does — a dead token is recorded, never thrown.
 *
 * This is a LOG OUT, not "Forget this device": other identities keep their
 * enrolments, and nobody's persistent grants or module registrations are
 * touched.
 */
export async function removeCurrentDeviceAndSignOut(
    deps: RemoveCurrentDeviceDeps, cid: string,
): Promise<LogoutAllResult> {
    await deps.removeCurrentDeviceRecord();
    deps.clearDeviceStorage(cid);
    const result = await endAllSessions(deps);
    deps.clearInMemoryState();
    return result;
}

/** One best-effort logout per stored token; failures are recorded, never thrown. */
async function endAllSessions(deps: DeviceSessionDeps): Promise<LogoutAllResult> {
    const endedSessions: string[] = [];
    const failedLogouts: string[] = [];
    for (const { cid, token } of deps.listStoredSessions()) {
        try {
            await deps.logout(token, cid);
            endedSessions.push(cid);
        } catch {
            failedLogouts.push(cid);
        }
    }
    return { endedSessions, failedLogouts };
}

/**
 * The sentence to show after "Forget this device" when some identity's server
 * device record survived, or null when everything was removed cleanly.
 */
export function serverRecordsLeftMessage(cids: string[]): string | null {
    if (cids.length === 0) return null;
    const who = cids.join(', ');
    const subject = cids.length === 1 ? 'The device record for' : 'Device records for';
    return `${subject} ${who} could not be removed — there was no live session for it. `
        + 'Log in as that identity and remove this device from its Devices pane.';
}
